import {
  RiCheckboxCircleLine,
  RiCheckLine,
  RiCloseCircleLine,
  RiErrorWarningLine,
  RiSendPlaneLine,
} from "@remixicon/react";
import { Alert, AlertDescription, AlertTitle } from "@screener/uikit/components/alert";
import { Badge } from "@screener/uikit/components/badge";
import { Button } from "@screener/uikit/components/button";
import { Input } from "@screener/uikit/components/input";
import { LoadingRegion } from "@screener/uikit/components/loading-skeleton";
import { PanelBlock } from "@screener/uikit/components/panel";
import { Skeleton } from "@screener/uikit/components/skeleton";
import { type ReactNode, useEffect, useRef } from "react";
import { SettingsGroupRow, SettingsRow } from "@/settings/settings-row";
import { SettingsSection } from "@/settings/settings-section";
import { isConfigured, type TelegramSettingsSnapshot } from "./use-telegram-settings";

/**
 * Разметка блока Telegram §6.6 (design.md решение 8, макет
 * `TelegramPanel.dc.html`, редизайн DEN-162) — чистый презентационный
 * компонент над снимком контроллера `use-telegram-settings.ts`. Ничего не
 * решает сам: каждое действие идёт наверх колбэком.
 */
export interface TelegramPanelViewProps {
  snapshot: TelegramSettingsSnapshot;
  onRetry: () => void;
  onTokenChange: (value: string) => void;
  onStartReplace: () => void;
  onCancelReplace: () => void;
  onChatIdChange: (value: string) => void;
  onIntervalChange: (value: string) => void;
  /** «Отменить»: черновик возвращается к сохранённому. */
  onDiscard: () => void;
  onSave: () => void;
  onTest: () => void;
  onRequestDisconnect: () => void;
  onCancelDisconnect: () => void;
  onConfirmDisconnect: () => void;
}

export const TELEGRAM_SECTION_LABEL = "Telegram-уведомления";
const TELEGRAM_DESCRIPTION = "Сообщения о срабатывании алертов приходят от вашего бота.";

/** Быстрые значения промежутка, секунды. */
const INTERVAL_PRESETS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "15", label: "15 с" },
  { value: "30", label: "30 с" },
  { value: "60", label: "60 с" },
  { value: "300", label: "5 мин" },
];

export function TelegramPanelView({
  snapshot,
  onRetry,
  onTokenChange,
  onStartReplace,
  onCancelReplace,
  onChatIdChange,
  onIntervalChange,
  onDiscard,
  onSave,
  onTest,
  onRequestDisconnect,
  onCancelDisconnect,
  onConfirmDisconnect,
}: TelegramPanelViewProps) {
  // При отказе сохранения фокус уходит на текст ошибки (ревью, второй круг,
  // пункт 3) — хук вызывается безусловно (правило хуков), эффект решает,
  // применять ли фокус, глядя на снимок каждый рендер.
  const saveErrorRef = useRef<HTMLParagraphElement | null>(null);
  const lastSaveErrorRef = useRef<string | null>(null);
  const saveErrorForFocus = snapshot.status === "ready" ? snapshot.saveError : null;
  useEffect(() => {
    if (saveErrorForFocus !== null && saveErrorForFocus !== lastSaveErrorRef.current) {
      saveErrorRef.current?.focus();
    }
    lastSaveErrorRef.current = saveErrorForFocus;
  }, [saveErrorForFocus]);

  if (snapshot.status === "loading") {
    return (
      <TelegramSection configured={false}>
        <PanelBlock>
          <LoadingRegion label="Читаем настройки Telegram…" className="flex flex-col gap-[18px]">
            {Array.from({ length: 3 }, (_, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: статичные декоративные поля
              <div key={index} className="flex flex-col gap-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-full" />
              </div>
            ))}
          </LoadingRegion>
        </PanelBlock>
      </TelegramSection>
    );
  }

  if (snapshot.status === "unavailable") {
    return (
      <TelegramSection configured={false}>
        <PanelBlock className="items-start">
          <p className="text-sm text-muted-foreground">
            Настройки Telegram не прочитались: демон недоступен.
          </p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Повторить
          </Button>
        </PanelBlock>
      </TelegramSection>
    );
  }

  const {
    view,
    draft,
    replacingToken,
    fieldError,
    canSave,
    draftDirty,
    saving,
    savedNotice,
    saveError,
    disconnecting,
  } = snapshot;
  const showTokenInput = !view.bot_token_set || replacingToken;
  const canDisconnect = view.bot_token_set || view.chat_id !== null;
  const configured = isConfigured(view);
  // Сохранение и отключение взаимоисключают друг друга (design.md решение 8, ревью):
  // пока идёт один из них, поля черновика и переключатели поля токена недоступны.
  const busy = saving || disconnecting;
  const testState = snapshot.testState;
  const hasNotices =
    fieldError !== null ||
    saveError !== null ||
    snapshot.disconnectError ||
    testState.kind !== "idle";

  const footer = (
    <>
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
        {canDisconnect && !snapshot.confirmingDisconnect ? (
          <Button
            variant="ghost-destructive"
            size="sm"
            disabled={saving}
            onClick={onRequestDisconnect}
          >
            Отключить Telegram
          </Button>
        ) : null}
        {draftDirty ? (
          <span className="text-xs text-muted-foreground">
            Проверяется сохранённое — сначала сохраните изменения.
          </span>
        ) : null}
        {savedNotice ? <span className="text-xs text-muted-foreground">сохранено</span> : null}
      </div>
      <div className="flex items-center gap-2">
        {draftDirty ? (
          <Button variant="ghost" size="sm" disabled={busy} onClick={onDiscard}>
            Отменить
          </Button>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          disabled={testState.kind === "testing" || saving || disconnecting}
          onClick={onTest}
        >
          <RiSendPlaneLine />
          Проверить
        </Button>
        <Button size="sm" className="font-semibold" disabled={!canSave} onClick={onSave}>
          Сохранить
        </Button>
      </div>
    </>
  );

  return (
    <TelegramSection configured={configured} footer={footer}>
      {showTokenInput ? (
        <SettingsRow
          label="Токен бота"
          hint="Токен выдаёт @BotFather. Напишите своему боту /start, иначе он не сможет вам писать."
        >
          <div className="flex items-center gap-2">
            <Input
              type="password"
              autoComplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              data-bwignore
              data-form-type="other"
              placeholder="вставьте токен от @BotFather"
              value={draft.token}
              disabled={busy}
              onChange={(event) => onTokenChange(event.target.value)}
            />
            {replacingToken ? (
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 text-muted-foreground"
                disabled={busy}
                onClick={onCancelReplace}
              >
                Отменить замену
              </Button>
            ) : null}
          </div>
        </SettingsRow>
      ) : (
        <SettingsGroupRow label="Токен бота" hint="После записи токен не показывается.">
          <div className="flex items-center gap-2">
            <Input
              readOnly
              aria-label="Токен бота"
              value="задан ••••••••••"
              className="max-w-[260px] text-muted-foreground"
            />
            <Button variant="outline" size="sm" disabled={busy} onClick={onStartReplace}>
              Заменить
            </Button>
          </div>
        </SettingsGroupRow>
      )}

      <SettingsRow label="Chat id" hint="Куда бот пишет. Для группы начинается с -100.">
        <Input
          placeholder="123456789"
          className="max-w-[260px] tabular-nums"
          value={draft.chatId}
          disabled={busy}
          onChange={(event) => onChatIdChange(event.target.value)}
        />
      </SettingsRow>

      <SettingsRow
        label="Промежуток"
        hint="Промежуток между сообщениями в секундах. Срабатывания внутри промежутка приходят одним сообщением списком."
      >
        <div className="relative w-[120px]">
          <Input
            inputMode="numeric"
            className="pr-7 tabular-nums"
            value={draft.interval}
            disabled={busy}
            onChange={(event) => onIntervalChange(event.target.value)}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-[11px] font-medium text-muted-foreground uppercase"
          >
            с
          </span>
        </div>
        <fieldset className="m-0 flex flex-wrap gap-1 border-0 p-0">
          <legend className="sr-only">Быстрый выбор промежутка</legend>
          {INTERVAL_PRESETS.map((preset) => (
            <Button
              key={preset.value}
              variant="toggle"
              size="tag"
              className="px-2"
              aria-pressed={draft.interval === preset.value}
              disabled={busy}
              onClick={() => onIntervalChange(preset.value)}
            >
              {preset.label}
            </Button>
          ))}
        </fieldset>
      </SettingsRow>

      {snapshot.confirmingDisconnect ? (
        <PanelBlock className="flex-row flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Стереть токен и chat id? Промежуток останется.
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onCancelDisconnect}>
              Оставить
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={snapshot.disconnecting || saving}
              onClick={onConfirmDisconnect}
            >
              Отключить
            </Button>
          </div>
        </PanelBlock>
      ) : null}

      {hasNotices ? (
        <PanelBlock>
          {fieldError !== null ? (
            <p role="alert" className="text-sm text-destructive">
              {fieldError}
            </p>
          ) : null}
          {saveError !== null ? (
            <p
              role="alert"
              tabIndex={-1}
              ref={saveErrorRef}
              className="text-sm text-destructive outline-none"
            >
              {saveError === "unavailable"
                ? "Демон недоступен — настройки не записаны."
                : "Демон не принял настройки — проверьте поля."}
            </p>
          ) : null}
          <TestOutcome testState={testState} />
          {snapshot.disconnectError ? (
            <p role="alert" className="text-sm text-destructive">
              Не удалось отключить Telegram — демон недоступен, повторите.
            </p>
          ) : null}
        </PanelBlock>
      ) : null}
    </TelegramSection>
  );
}

function TelegramSection({
  configured,
  footer,
  children,
}: {
  configured: boolean;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <SettingsSection
      sectionId="telegram"
      icon={<RiSendPlaneLine className="size-4" />}
      label={TELEGRAM_SECTION_LABEL}
      description={TELEGRAM_DESCRIPTION}
      action={<ConfiguredBadge configured={configured} />}
      footer={footer}
    >
      {children}
    </SettingsSection>
  );
}

function ConfiguredBadge({ configured }: { configured: boolean }) {
  return configured ? (
    <Badge variant="success" size="lg">
      <RiCheckLine className="size-3" />
      настроен
    </Badge>
  ) : (
    <Badge variant="dashed" size="lg">
      не настроен
    </Badge>
  );
}

function TestOutcome({
  testState,
}: {
  testState: Extract<TelegramSettingsSnapshot, { status: "ready" }>["testState"];
}) {
  if (testState.kind === "idle" || testState.kind === "testing") {
    return (
      <div role="status" className="empty:hidden">
        {testState.kind === "testing" ? (
          <p className="text-sm text-muted-foreground">Проверяем…</p>
        ) : null}
      </div>
    );
  }

  if (testState.kind === "daemon_unavailable") {
    return (
      <Alert variant="error">
        <AlertDescription>Демон недоступен — проверка не выполнена.</AlertDescription>
      </Alert>
    );
  }

  const { result } = testState;
  if (result.outcome === "sent") {
    return (
      <div
        role="status"
        className="flex items-start gap-2 rounded-lg bg-success-wash px-3 py-2 text-success"
      >
        <RiCheckboxCircleLine className="mt-0.5 size-4 shrink-0" />
        <div>
          <p className="text-xs font-medium">Отправлено</p>
          <p className="text-xs text-muted-foreground">Проверочное сообщение ушло в ваш чат.</p>
        </div>
      </div>
    );
  }

  if (result.outcome === "not_configured") {
    return (
      <div
        role="status"
        className="flex items-start gap-2 rounded-lg border border-dashed border-input px-3 py-2 text-muted-foreground"
      >
        <RiErrorWarningLine className="mt-0.5 size-4 shrink-0" />
        <div>
          <p className="text-xs font-medium">Не отправлено</p>
          <p className="text-xs">Сначала задайте токен и chat id.</p>
        </div>
      </div>
    );
  }

  if (result.outcome === "rejected") {
    return (
      <Alert variant="error">
        <RiCloseCircleLine aria-hidden="true" />
        <div className="flex flex-col gap-1.5">
          <AlertTitle>Telegram отказал</AlertTitle>
          <AlertDescription className="rounded-md bg-error/10 px-2 py-1.5 text-sm text-foreground">
            {result.description}
          </AlertDescription>
          <AlertDescription>Ответ Telegram приведён как есть.</AlertDescription>
        </div>
      </Alert>
    );
  }

  return (
    <Alert variant="warning">
      <RiErrorWarningLine aria-hidden="true" />
      <div>
        <AlertTitle>Telegram недоступен</AlertTitle>
        <AlertDescription>Причина: {result.reason} — повторите позже.</AlertDescription>
      </div>
    </Alert>
  );
}
